<template>
  <div>
    <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Audit History</h3>

    <div v-if="loading" class="animate-pulse space-y-2">
      <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
    </div>

    <div v-else-if="audits.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
      No audit history yet.
    </div>

    <div v-else class="space-y-2">
      <div v-for="(audit, idx) in audits" :key="audit.id"
        class="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer"
        @click="$emit('view-audit', audit)">
        <div class="flex items-center gap-3">
          <div class="text-lg font-bold" :class="scoreColor(audit.score)">
            {{ audit.score ?? '—' }}
          </div>
          <div>
            <p class="text-xs text-gray-700 dark:text-gray-300">{{ formatDate(audit.created_at) }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ audit.pass_count }}p / {{ audit.warning_count }}w / {{ audit.fail_count }}f
            </p>
          </div>
        </div>
        <div v-if="idx < audits.length - 1" class="text-xs font-medium" :class="trendClass(audit, audits[idx + 1])">
          {{ trendLabel(audit, audits[idx + 1]) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getAuditHistory } from '~/javascript/api/page_audits'

const props = defineProps({
  pageId: { type: String, required: true }
})

defineEmits(['view-audit'])

const audits = ref([])
const loading = ref(true)

function scoreColor(score) {
  if (score == null) return 'text-gray-400'
  if (score >= 80) return 'text-green-600 dark:text-green-400'
  if (score >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function trendLabel(current, previous) {
  if (current.score == null || previous.score == null) return ''
  const diff = current.score - previous.score
  if (diff > 0) return `+${diff}`
  if (diff < 0) return `${diff}`
  return '='
}

function trendClass(current, previous) {
  if (current.score == null || previous.score == null) return 'text-gray-400'
  const diff = current.score - previous.score
  if (diff > 0) return 'text-green-600 dark:text-green-400'
  if (diff < 0) return 'text-red-600 dark:text-red-400'
  return 'text-gray-400'
}

async function fetchHistory() {
  try {
    loading.value = true
    const { data } = await getAuditHistory(props.pageId)
    audits.value = data || []
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch audit history:', e)
  } finally {
    loading.value = false
  }
}

const refresh = () => fetchHistory()
defineExpose({ refresh })

onMounted(fetchHistory)
</script>
